import { stripEmojis } from "./content-polish"
import { MARKDOWN_IMAGE_BARE } from "./markdown-image"

/** Longest card description that fits the docs home without truncating. */
export const CARD_SUMMARY_MAX = 65

/**
 * Card summaries sit next to each other, so one ending on a period and the next
 * on nothing reads as a mistake. Every summary the import writes goes through
 * here, whoever wrote it.
 */
export function ensureSentence(value: string): string {
	const clean = value
		.replace(/[\u2018\u2019]/g, "'")
		.replace(/[\u201c\u201d]/g, '"')
		.replace(/\s+/g, " ")
		.trim()
	if (!clean) return ""
	return /[.!?]$/.test(clean) ? clean : `${clean}.`
}

/** Longest group name the portal sidebar shows without truncating. */
export const GROUP_NAME_MAX = 25

const TRAILING_JUNK = /[\s,;:.\-–—]+$/
const DANGLING_WORD =
	/\s+(?:and|&|or|the|a|an|to|of|for|with|in|on|at|by|is|are|that|this)$/i

// Text that already fits keeps its own punctuation; only a clipped fragment
// gets tidied, so the set never mixes "...you set." with "...you set".
function clip(text: string, max: number): string {
	const clean = text.replace(/\s+/g, " ").trim()
	if (clean.length <= max) return clean

	const cut = clean.slice(0, max + 1)
	const lastSpace = cut.lastIndexOf(" ")
	let result = (lastSpace > 0 ? cut.slice(0, lastSpace) : clean.slice(0, max))
		.replace(TRAILING_JUNK, "")
		.replace(DANGLING_WORD, "")
	// A single word longer than the budget still has to fit.
	if (!result) result = clean.slice(0, max)
	return result.replace(TRAILING_JUNK, "")
}

function toPlain(markdown: string): string {
	return stripEmojis(
		markdown
			.replace(MARKDOWN_IMAGE_BARE, "")
			.replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
			.replace(/`([^`]*)`/g, "$1")
			.replace(/^[#>\-*\d.\s]+/, "")
			.replace(/[*_~]/g, "")
			.replace(/\s+/g, " ")
			.trim(),
	)
}

const WEAK_SUMMARY = /^(overview|summary|introduction|intro|about|general)$/i

/**
 * Card descriptions read as a set, so they share one length. An existing summary
 * wins unless it says nothing, in which case the body's first real sentence does.
 */
export function buildCardSummary(
	existingSummary: string | undefined,
	markdown: string,
	max: number = CARD_SUMMARY_MAX,
): string | undefined {
	const paragraphs = markdown.split(/\n\s*\n/).map(toPlain)
	const candidates = [
		...(existingSummary ? [toPlain(existingSummary)] : []),
		...paragraphs,
	]

	const chosen = candidates.find(
		(candidate) => candidate.length >= 20 && !WEAK_SUMMARY.test(candidate),
	)
	if (!chosen) return undefined

	return clip(chosen, max)
}

/**
 * Sidebar names are one line. Everything after a colon or the first comma is a
 * subtitle the reader does not need, so it goes before any hard clipping.
 */
export function shortenGroupName(
	name: string,
	max: number = GROUP_NAME_MAX,
): string {
	let short = name
		.replace(/\s+and\s+/gi, " & ")
		.replace(/\s+/g, " ")
		.trim()

	const colon = short.indexOf(":")
	if (colon > 0 && colon <= max) short = short.slice(0, colon)

	if (short.length > max) {
		const comma = short.indexOf(",")
		if (comma > 0) short = short.slice(0, comma)
	}

	return clip(short, max)
}

interface IconRule {
	pattern: RegExp
	icon: string
}

// Every name here exists as `Icon<name>Fill18` in nucleo-ui-fill-18.
const ICON_RULES: IconRule[] = [
	{
		pattern: /\b(report|analytic|insight|metric|statistic)/i,
		icon: "ChartBar",
	},
	{
		pattern:
			/\b(pricing|price|plans?|billing|invoice|payment|subscription|refund)\b/i,
		icon: "CreditCard",
	},
	{
		pattern:
			/\b(error|fail|troubleshoot|not working|problem|issue|why (can|are|were|is))/i,
		icon: "TriangleWarning",
	},
	{ pattern: /\b(video|reel|recording)/i, icon: "Video" },
	{ pattern: /\b(image|photo|picture|thumbnail|gif)/i, icon: "Image" },
	{
		pattern: /\b(calendar|schedul|queue|planner|timing|best time)/i,
		icon: "Calendar",
	},
	{
		pattern: /\b(team|user|member|permission|role|collaborat|invite)/i,
		icon: "Users",
	},
	{
		pattern: /\b(api|webhook|developer|integration|connect|zapier)/i,
		icon: "SquaresConnected",
	},
	{ pattern: /\b(notification|alert|remind)/i, icon: "Bell" },
	{ pattern: /\b(export|download|backup)/i, icon: "Download" },
	{ pattern: /\b(upload|import)/i, icon: "Upload" },
	{ pattern: /\b(delete|remove|archive|trash)/i, icon: "Trash" },
	{ pattern: /\b(setting|configur|setup|set up|manage)/i, icon: "Gear" },
	{ pattern: /\b(search|find|filter)/i, icon: "Magnifier" },
	{ pattern: /\b(links?|url|bio)\b/i, icon: "Link3" },
	{ pattern: /\b(tag|label|mention|hashtag)/i, icon: "Tag" },
	{ pattern: /\b(location|map|place|store)/i, icon: "MapPin" },
	{ pattern: /\b(email|mail|inbox)/i, icon: "Envelope" },
	{
		pattern: /\b(comment|reply|message|chat|dm|conversation)/i,
		icon: "Messages",
	},
	{ pattern: /\b(mobile|phone|ios|android|apps?)\b/i, icon: "Mobile" },
	{ pattern: /\b(approv|review|sign off)/i, icon: "CheckDouble" },
	{ pattern: /\b(library|folder|asset|media)/i, icon: "Folder" },
	{
		pattern: /\b(security|password|login|sso|two.factor|authenticat)/i,
		icon: "Lock",
	},
	{ pattern: /\b(getting started|start|first|welcome|onboard)/i, icon: "Flag" },
	{
		pattern: /\b(tip|trick|best practice|idea|inspiration)/i,
		icon: "Lightbulb",
	},
	{ pattern: /\b(spec|size|dimension|requirement|limit)/i, icon: "Ruler" },
	{ pattern: /\b(listen|monitor|track|watch)/i, icon: "Radar" },
	{
		pattern: /\b(announce|release|new in|update|changelog)/i,
		icon: "Megaphone",
	},
	{
		pattern: /\b(guide|course|learn|tutorial|academy|expert)/i,
		icon: "GraduationCap",
	},
	{
		pattern: /\b(draft|write|caption|compose|create|content)/i,
		icon: "PenWriting",
	},
	{
		pattern: /\b(policy|standard|terms|privacy|legal|compliance)/i,
		icon: "Shield",
	},
	{ pattern: /\b(about|who|company|history|mission)/i, icon: "CircleInfo" },
]

export function pickArticleIcon(title: string, fallback: string): string {
	return ICON_RULES.find((rule) => rule.pattern.test(title))?.icon ?? fallback
}

interface IconFamilyRule {
	pattern: RegExp
	icons: string[]
}

// Ordered specific-first. Each bucket lists every closely-related Nucleo fill
// icon, so a page of similar articles (ten calendar pages, say) each gets a
// distinct but still on-topic glyph instead of colliding on one.
const ICON_FAMILY_RULES: IconFamilyRule[] = [
	{
		pattern: /calendar|schedul|queue|planner|recurring|autosave/i,
		icons: [
			"Calendar",
			"CalendarCheck",
			"CalendarStar",
			"CalendarClock",
			"CalendarHeart",
			"CalendarBookmark",
			"CalendarUser",
			"CalendarLink",
			"CalendarDownload",
			"CalendarSearch",
			"CalendarAlert",
			"CalendarBolt",
			"CalendarGrid",
			"CalendarPhone",
			"CalendarSettings",
			"CalendarPen",
			"CalendarRefresh",
			"CalendarKey",
			"CalendarCloud",
			"CalendarShield",
		],
	},
	{
		pattern: /analytic|metric|statistic|insight/i,
		icons: [
			"ChartBar",
			"ChartArea",
			"ChartActivity",
			"ChartPie",
			"ChartPie2",
			"Chart2",
			"Chart3",
			"ChartActivity2",
			"ChartActivity3",
			"ChartArea2",
			"ChartRadar",
		],
	},
	{
		pattern: /\bvideo\b|reel|recording/i,
		icons: [
			"Video",
			"Video2",
			"MonitorVideo",
			"LaptopVideo",
			"SplitVideo",
			"PresentationScreenVideo",
		],
	},
	{
		pattern: /\bimage|photo|thumbnail|cover image/i,
		icons: [
			"Image",
			"PhotoAlbum",
			"PhotoEditor",
			"PhotoFrame",
			"CircleImage",
			"HexagonImage",
			"Photo",
			"Photos",
		],
	},
	{
		pattern: /\bcamera\b|screenshot/i,
		icons: ["Camera", "Camera2", "Camera3", "CameraSparkle"],
	},
	{
		pattern: /notification|alert|remind/i,
		icons: ["Bell", "BellDot", "BellOn", "BellPlus", "BellSnooze"],
	},
	{
		pattern: /\bcollaborator|permission|access\b/i,
		icons: [
			"CircleUser",
			"CircleUserCheck",
			"SquareUser",
			"UserSearch",
			"UserSettings",
			"UserShield",
			"UserGroup",
		],
	},
	{
		pattern: /security|password|authenticat|two.factor|\b2fa\b|protection/i,
		icons: [
			"GlobeLock",
			"FileShield",
			"FolderShield",
			"BoxShield",
			"EarthShield",
			"HouseShield",
			"LawShield",
		],
	},
	{
		pattern: /\blogin\b|\bsign.?in\b/i,
		icons: [
			"CircleKey",
			"FileKey",
			"FolderKey",
			"GearKeyhole",
			"DatabaseKey",
			"CartKey",
			"CircleKey",
		],
	},
	{
		pattern: /\blink\b|\burl\b|\bbio\b/i,
		icons: [
			"Link",
			"ExternalLink",
			"FileLink",
			"FolderLink",
			"HouseLink",
			"BagLink",
			"DependencyLink",
		],
	},
	{
		pattern: /\btag|label|hashtag|mention/i,
		icons: ["Hashtag", "CircleHashtag", "DiscountTag"],
	},
	{
		pattern: /location|geolocation|\bmap\b/i,
		icons: [
			"Map",
			"MapAlert",
			"MapCheck",
			"MapClock",
			"MapHeart",
			"MapDownload",
			"MapCursor",
			"MapLink",
			"MapRefresh",
			"PinRefresh",
		],
	},
	{
		pattern: /\bemail\b|\bmail\b/i,
		icons: [
			"Envelope",
			"EnvelopeAlert",
			"EnvelopeCheck",
			"EnvelopeClock",
			"EnvelopeHeart",
			"EnvelopeArrowRight",
			"EnvelopeOpen",
		],
	},
	{
		pattern: /\binbox\b/i,
		icons: ["Inbox", "InboxArrowDown", "InboxArrowUp", "InboxStack"],
	},
	{
		pattern: /comment|reply|conversation|message|chat|\bdm\b/i,
		icons: [
			"Message",
			"MessageCheck",
			"MessageClock",
			"MessageFlag",
			"MessageArrowRight",
			"ChatBubble",
			"ChatBubbleCheck",
			"ChatBubbleHeart",
			"ChatBubbleClock",
			"ChatBubbleFlag",
			"ChatBubbleForward",
			"ChatBubbleStar",
		],
	},
	{
		pattern: /\bmobile\b|\bios\b|\bandroid\b|\bapp\b/i,
		icons: [
			"Mobile",
			"Mobile2",
			"MobileCircuit",
			"MobileHeart",
			"MobileSignal",
			"MobileWave",
			"LaptopMobile",
		],
	},
	{
		pattern:
			/pricing|\bprice\b|plans?\b|billing|invoice|payment|subscription|refund|tax/i,
		icons: [
			"CreditCard",
			"CreditCardAlert",
			"CreditCardCheck",
			"CreditCardHeart",
			"CreditCardLock",
			"CreditCardContactless",
			"CreditCardContactless2",
			"Cards",
			"CardSparkle",
		],
	},
	{
		pattern: /setting|configur|setup|set up|manage/i,
		icons: ["Gear", "Gear2", "Gear3", "Gear4", "GearArrowRight", "GearNodes"],
	},
	{
		pattern: /library|asset|archive/i,
		icons: [
			"Folder",
			"Folder2",
			"Folder3",
			"Folder4",
			"Folder5",
			"FolderAlert",
			"Folder2Open",
		],
	},
	{
		pattern: /publish|post\b/i,
		icons: [
			"CirclePlay",
			"ClapperboardPlay",
			"FilmPlay",
			"FolderPlay",
			"CloudPlay",
			"FilePlay",
		],
	},
	{
		pattern: /music|audio|sound/i,
		icons: [
			"Music",
			"MusicAlbum",
			"MusicNote",
			"MusicNote2",
			"CircleMusicNote",
			"FolderMusic",
			"FileMusic",
		],
	},
	{
		pattern: /search|find\b/i,
		icons: [
			"Magnifier",
			"Magnifier2",
			"Magnifier3",
			"MagnifierAlert",
			"MagnifierCheck",
			"MagnifierStar",
			"ArchiveSearch",
			"BagSearch",
			"DatabaseSearch",
			"EarthSearch",
			"BoxSearch",
		],
	},
	{
		pattern: /filter\b/i,
		icons: [
			"Filter",
			"Filter2",
			"BarsFilter",
			"ArrowsFilter",
			"CircleBarsFilter",
		],
	},
	{
		pattern: /export|download|backup/i,
		icons: [
			"Download",
			"CloudDownload",
			"CloudDownload2",
			"ArchiveDownload",
			"BoxArchiveDownload",
			"CalendarDownload",
			"DesignFileDownload",
		],
	},
	{
		pattern: /upload|import\b/i,
		icons: [
			"Upload",
			"Upload2",
			"Upload3",
			"Upload4",
			"CloudUpload",
			"CloudUpload2",
			"SquareDashedUpload",
		],
	},
	{
		pattern: /delete|remove|duplicate|cancel/i,
		icons: ["Trash", "Trash2", "TrashContent", "TrashSlash", "TrashXmark"],
	},
	{
		pattern: /polic|standard|terms|privacy|legal|complian|protect/i,
		icons: [
			"BoxShield",
			"CalendarShield",
			"EarthShield",
			"FileShield",
			"FolderShield",
			"HouseShield",
			"LawShield",
		],
	},
	{
		pattern: /\btip|best practice|expert/i,
		icons: [
			"FileStar",
			"FolderStar",
			"HouseStar",
			"MagnifierStar",
			"ChatBubbleStar",
			"CalendarStar",
		],
	},
	{
		pattern: /getting started|welcome|start\b|onboard|home\b/i,
		icons: [
			"Flag",
			"Flag2",
			"Flag3",
			"Flag4",
			"Flag5",
			"Flag6",
			"Flag7",
			"ChatBubbleFlag",
		],
	},
	{
		pattern: /guide|course|learn|tutorial|academy|template/i,
		icons: [
			"Book",
			"Book2",
			"BookBookmark",
			"BookOpen",
			"BookOpen2",
			"BookOpen3",
			"BookOpen4",
			"BookOpen5",
			"AddressBook",
			"ArchiveBookmark",
		],
	},
	{ pattern: /expert/i, icons: ["GraduationCap"] },
	{ pattern: /announce|launch|new feature|release/i, icons: ["Megaphone"] },
	{
		pattern: /listen|monitor|track|watch/i,
		icons: ["Radar", "Radar2", "ChartRadar"],
	},
	{
		pattern: /idea/i,
		icons: ["Lightbulb", "Lightbulb2", "Lightbulb3", "LightbulbSparkle"],
	},
	{
		pattern: /\bai\b|magic|generat/i,
		icons: ["Atom", "Sparkle3", "Blueprint", "Shapes"],
	},
	{
		pattern: /platform|website|traffic/i,
		icons: ["Globe", "Globe2", "Globe3", "GlobePointer", "GlobeSearch"],
	},
	{
		pattern: /view\b|preview/i,
		icons: ["Eye", "Eye2", "EyeClosed", "EyeDropper", "EyeLens"],
	},
	{
		pattern: /board\b|carousel|slideshow/i,
		icons: [
			"Grid",
			"Grid2",
			"Grid2x2",
			"Grid3",
			"Grid3x3",
			"CalendarGrid",
			"CircleGridDots",
		],
	},
	{
		pattern: /discover|explore/i,
		icons: [
			"Compass",
			"Compass2",
			"Compass3",
			"Compass4",
			"Compass5",
			"DrawCompass",
		],
	},
	{ pattern: /launch|early access/i, icons: ["Flag2"] },
	{ pattern: /success/i, icons: ["Trophy"] },
	{
		pattern: /campaign|reach|engagement/i,
		icons: ["Target", "MarketingTarget"],
	},
	{
		pattern: /specification|\bsize\b|dimension|resolution|format|crop/i,
		icons: ["Ruler", "RulerPen", "RulerPen2", "RulerTriangle", "RulerCursor"],
	},
	{ pattern: /\bhashtag\b/i, icons: ["Hashtag", "CircleHashtag"] },
	{ pattern: /\bat\b mention/i, icons: ["AtSign"] },
	{
		pattern: /\bwhy\b|\bwhat is\b|faq/i,
		icons: [
			"AlertQuestion",
			"CircleQuestion",
			"OctagonQuestion",
			"SquareQuestion",
		],
	},
	{
		pattern: /integrat|connect\b/i,
		icons: [
			"SquaresConnected",
			"ArrowsLeftRightTrail",
			"Link3",
			"Nodes",
			"Nodes2",
		],
	},
	{
		pattern: /sync\b/i,
		icons: [
			"Refresh",
			"BagRefresh",
			"CalendarRefresh",
			"CartRefresh",
			"CloudRefresh",
			"FileRefresh",
			"FolderRefresh",
		],
	},
	{
		pattern: /report\b|spreadsheet/i,
		icons: ["Table", "SquareTable", "PivotTable", "PicnicTable"],
	},
	{ pattern: /drive\b|dropbox|onedrive/i, icons: ["HardDrive"] },
	{
		pattern: /bot\b|zapier/i,
		icons: ["Sitemap", "Sitemap2", "Route", "Gauge", "Gauge2"],
	},
	{
		pattern: /caption|alt text/i,
		icons: ["Text", "TextA", "ScanText", "ScrollText"],
	},
	{
		pattern: /writ|draft|compose|edit\b|annotat/i,
		icons: [
			"ArchivePencil",
			"BagPen",
			"CalendarPen",
			"CartPen",
			"ChatBubblePen",
		],
	},
	{
		pattern: /color|colour/i,
		icons: ["ColorPalette", "ColorPalette2", "Palette", "Palette2"],
	},
	{ pattern: /crop\b/i, icons: ["Crop", "CropRotate"] },
	{
		pattern: /approv|review/i,
		icons: [
			"BadgeCheck",
			"BadgeCheck2",
			"BookmarkCheck",
			"Box2Check",
			"BoxCheck",
			"CalendarCheck",
			"ArchiveCheck",
		],
	},
	{
		pattern: /time\b|history/i,
		icons: [
			"AlarmClock",
			"ArchiveClock",
			"ClockTime",
			"Timer",
			"Timer2",
			"TimerProgress",
		],
	},
	{
		pattern: /error|troubleshoot|not working|issue|fail|missing|problem/i,
		icons: [
			"AlertWarning",
			"CircleWarning",
			"OctagonWarning",
			"SquareWarning",
			"TriangleWarning",
		],
	},
	{
		pattern: /team\b|teammate/i,
		icons: [
			"CircleUserSparkle",
			"SquareUserSparkle",
			"UserSearch",
			"UserSettings",
			"UserShield",
			"UserGroup",
		],
	},
	{
		pattern: /affiliate|referral/i,
		icons: [
			"Coin",
			"Bitcoin",
			"Bitcoin2",
			"CircleBitcoin",
			"CircleCoinIn",
			"CircleCoinOut",
			"CircleCoinPlus",
		],
	},
	{
		pattern: /method\b/i,
		icons: [
			"Wallet",
			"Wallet2",
			"Wallet3",
			"WalletCheck",
			"WalletCoin",
			"Wallet2Check",
		],
	},
	{
		pattern: /invoice/i,
		icons: ["Receipt", "Receipt2", "Receipt3", "Receipt4"],
	},
]

// Backstop once an article's own keywords match nothing, or its family runs
// dry. Deliberately generic: no CalendarX/ChartX/EnvelopeX-style compound here,
// so a leftover icon never plants an unrelated theme (a calendar glyph) on an
// article about something else entirely.
const FALLBACK_ICON_POOL: string[] = [
	"Layers",
	"Layers2",
	"Layers3",
	"LayersStacked",
	"LayersMinus",
	"LayersPlus",
	"Grid",
	"Grid2",
	"Grid2x2",
	"Grid3",
	"Grid3x3",
	"Compass",
	"Compass2",
	"Compass3",
	"Compass4",
	"Compass5",
	"PuzzlePiece",
	"Shapes2",
	"Trophy",
	"Medal",
	"MilitaryMedal",
	"Target",
	"Globe",
	"Globe2",
	"Globe3",
	"Eye",
	"Eye2",
	"Book",
	"Book2",
	"BookBookmark",
	"BookOpen",
	"BookOpen2",
	"BookOpen3",
	"BookOpen4",
	"BookOpen5",
	"AddressBook",
	"Folder",
	"Folder2",
	"Folder3",
	"Folder4",
	"Folder5",
	"Flag",
	"Flag2",
	"Flag3",
	"Flag4",
	"Flag5",
	"Flag6",
	"Flag7",
	"Bolt",
	"BoltLightning",
	"BoltSpeed",
	"Lightbulb",
	"Lightbulb2",
	"Lightbulb3",
	"LightbulbSparkle",
	"Atom",
	"Sparkle3",
	"Blueprint",
	"Shapes",
	"GraduationCap",
	"Megaphone",
	"Radar",
	"Radar2",
	"ColorPalette",
	"ColorPalette2",
	"Palette",
	"Palette2",
	"Ruler",
	"RulerPen",
	"RulerPen2",
	"RulerTriangle",
	"Sitemap",
	"Sitemap2",
	"Route",
	"Gauge",
	"Gauge2",
	"Speedometer",
	"Cube",
	"Cube2",
	"Cubes",
	"Text",
	"TextA",
	"ScrollText",
	"HardDrive",
	"Timer",
	"Timer2",
	"TimerProgress",
	"AlarmClock",
	"Wallet",
	"Wallet2",
	"Wallet3",
	"WalletCheck",
	"WalletCoin",
	"Receipt",
	"Receipt2",
	"Receipt3",
	"Receipt4",
	"Percentage",
	"CirclePercentage",
	"Coin",
	"Bitcoin",
	"Bitcoin2",
	"Hashtag",
	"CircleHashtag",
	"AtSign",
	"AlertQuestion",
	"CircleQuestion",
	"SquareQuestion",
	"Nodes3",
	"Nodes4",
	"Repeat",
	"Repeat2",
	"Repeat3",
	"Repeat4",
	"RepeatOnce",
	"Table",
	"SquareTable",
	"Music",
	"MusicAlbum",
	"MusicNote",
	"MusicNote2",
	"Trash2Content",
	"Magnifier2",
	"Magnifier3",
	"Crop",
	"CropRotate",
	"Wrench",
	"Voicemail",
]

export interface IconAssignmentItem {
	id: string
	title: string
}

export interface IconAssignmentOptions {
	pool?: readonly string[]
	/** Repeat a glyph once the pool runs dry instead of throwing. */
	allowRepeats?: boolean
}

/**
 * Assigns every item a distinct icon. Tries each item's own keyword matches
 * first, then falls back to the shared pool, so related articles land on
 * related-but-different glyphs rather than repeating one icon 30 times.
 * Throws once the pool runs dry unless `allowRepeats` says otherwise.
 */
export function assignUniqueIcons(
	items: readonly IconAssignmentItem[],
	{
		pool = FALLBACK_ICON_POOL,
		allowRepeats = false,
	}: IconAssignmentOptions = {},
): Record<string, string> {
	const used = new Set<string>()
	const result: Record<string, string> = {}

	for (const item of items) {
		const candidates = [
			...ICON_FAMILY_RULES.filter((rule) =>
				rule.pattern.test(item.title),
			).flatMap((rule) => rule.icons),
			...pool,
		]
		const icon = candidates.find((candidate) => !used.has(candidate))
		if (!icon) {
			if (!allowRepeats) {
				throw new Error(
					`No unique icon left for "${item.title}" after assigning ${used.size}; the pool has ${pool.length} entries.`,
				)
			}
			result[item.id] = pickArticleIcon(item.title, candidates[0] ?? "FileText")
			continue
		}
		used.add(icon)
		result[item.id] = icon
	}

	return result
}
